/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */ import { useRef } from 'react';
import { mergeProps, useDescription, useGlobalListeners } from '../utils';
import { usePress } from './usePress';
const DEFAULT_THRESHOLD = 500;
/**
 * Handles long press interactions across mouse and touch devices. Supports a customizable time threshold,
 * accessibility description, and normalizes behavior across browsers and devices.
 */ export function useLongPress(props) {
    const { isDisabled, onLongPressStart, onLongPressEnd, onLongPress, threshold = DEFAULT_THRESHOLD, accessibilityDescription } = props;
    const timeRef = useRef(null);
    const { addGlobalListener, removeGlobalListener } = useGlobalListeners();
    const { pressProps } = usePress({
        isDisabled,
        onPressStart (e) {
            e.continuePropagation();
            if (e.pointerType === 'mouse' || e.pointerType === 'touch') {
                if (onLongPressStart) {
                    onLongPressStart({
                        ...e,
                        type: 'longpressstart'
                    });
                }
                timeRef.current = setTimeout(()=>{
                    // Prevent other usePress handlers from also handling this event.
                    e.target.dispatchEvent(new PointerEvent('pointercancel', {
                        bubbles: true
                    }));
                    if (onLongPress) {
                        onLongPress({
                            ...e,
                            type: 'longpress'
                        });
                    }
                    timeRef.current = null;
                }, threshold);
                // Prevent context menu, which may be opened on long press on touch devices
                if (e.pointerType === 'touch') {
                    const onContextMenu = (e)=>{
                        e.preventDefault();
                    };
                    addGlobalListener(e.target, 'contextmenu', onContextMenu, {
                        once: true
                    });
                    addGlobalListener(window, 'pointerup', ()=>{
                        // If no contextmenu event is fired quickly after pointerup, remove the handler
                        // so future context menu events outside a long press are not prevented.
                        setTimeout(()=>{
                            removeGlobalListener(e.target, 'contextmenu', onContextMenu);
                        }, 30);
                    }, {
                        once: true
                    });
                }
            }
        },
        onPressEnd (e) {
            if (timeRef.current) {
                clearTimeout(timeRef.current);
            }
            if (onLongPressEnd && (e.pointerType === 'mouse' || e.pointerType === 'touch')) {
                onLongPressEnd({
                    ...e,
                    type: 'longpressend'
                });
            }
        }
    });
    const descriptionProps = useDescription(onLongPress && !isDisabled && accessibilityDescription ? accessibilityDescription : '');
    return {
        longPressProps: mergeProps(pressProps, descriptionProps)
    };
}

//# sourceMappingURL=useLongPress.js.map